// New work for a customer already on file (Jacky, 14 Oct 2026): a builder with an account brings a
// new site. The only door was "Log a phone / walk-in lead", which asks for the person again and
// matches by phone/email. This names the customer by id and opens the Opportunity in one action
// (open_opportunity_for_contact), then lands on it — the Proposal starts from there.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, cn } from "@aiw/ui";
import type { MessageId } from "@aiw/i18n";
import { useProposal } from "../hooks/slice-a";
import { useT } from "../lib/i18n";
import { inputCls, selectCls } from "./job-bits";
import { CATEGORIES } from "./lead-new-form";

const SOURCES: [string, MessageId][] = [["trade_network", "newOpp.source.trade_network"], ["referral", "newOpp.source.referral"], ["phone", "newOpp.source.phone"], ["email", "newOpp.source.email"], ["website", "newOpp.source.website"], ["other", "newOpp.source.other"]];

export function NewOpportunityButton({ contactId, name, compact = false }: { contactId: string; name: string; compact?: boolean }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  return <>
    <Button size="sm" variant={compact ? "outline" : "default"} onClick={() => setOpen(true)}>{compact ? t("newOpp.forPerson") : t("newOpp.button")}</Button>
    {open && <NewOpportunityDialog contactId={contactId} name={name} onClose={() => setOpen(false)} />}
  </>;
}

function NewOpportunityDialog({ contactId, name, onClose }: { contactId: string; name: string; onClose: () => void }) {
  const { t } = useT();
  const navigate = useNavigate();
  const propose = useProposal();
  const [f, setF] = useState({ site_address: "", suburb: "", postcode: "", category: "", source_id: "trade_network", raw_brief: "" });
  const [notice, setNotice] = useState("");
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF((s) => ({ ...s, [k]: e.target.value }));
  const canSubmit = f.site_address.trim().length > 0 && !propose.busy;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setNotice("");
    const r = await propose.run("open_opportunity_for_contact", {
      contact_id: contactId, site_address: f.site_address.trim(), suburb: f.suburb.trim() || null, postcode: f.postcode.trim() || null,
      category: f.category || null, raw_brief: f.raw_brief.trim(), source_id: f.source_id,
    }, t("newOpp.done"), (reason) => setNotice(t("newOpp.failed", { reason })));
    if (r?.status === "executed") navigate(`/opportunities/${(r.result as { ref: string }).ref}`);
    else if (r?.status === "pending") setNotice(t("newOpp.pending"));
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display">{t("newOpp.title", { name })}</DialogTitle>
          <DialogDescription>{t("newOpp.lede")}</DialogDescription>
        </DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <Field label={t("newOpp.siteAddress")}><input className={inputCls} value={f.site_address} onChange={set("site_address")} autoFocus /></Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid grid-cols-[1fr_5.5rem] gap-2">
              <Field label={t("newOpp.suburb")}><input className={inputCls} value={f.suburb} onChange={set("suburb")} /></Field>
              <Field label={t("newOpp.postcode")}><input className={inputCls} inputMode="numeric" value={f.postcode} onChange={set("postcode")} /></Field>
            </div>
            <Field label={t("newOpp.category")}><select className={cn(selectCls, "w-full")} value={f.category} onChange={set("category")}><option value="">{t("newOpp.categoryUnsure")}</option>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
            <Field label={t("newOpp.source")}><select className={cn(selectCls, "w-full")} value={f.source_id} onChange={set("source_id")}>{SOURCES.map(([k, id]) => <option key={k} value={k}>{t(id)}</option>)}</select></Field>
          </div>
          <Field label={t("newOpp.brief")}><textarea className="min-h-[72px] w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring" placeholder={t("newOpp.briefHint")} value={f.raw_brief} onChange={set("raw_brief")} /></Field>
          {notice && <p role="alert" className="text-sm text-destructive">{notice}</p>}
          <div className="flex items-center gap-2 pt-1">
            <Button type="submit" disabled={!canSubmit}>{t("newOpp.submit")}</Button>
            <Button type="button" variant="ghost" onClick={onClose}>{t("newOpp.cancel")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block text-micro text-muted-foreground"><span className="mb-1 block">{label}</span>{children}</label>;
}
